import { ChevronDown, ChevronRight, ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import { Fragment, useState } from 'react';
import { Pagination, Spinner } from '@/components/ui';
import { cn } from '@/utils/cn';
import { SyncJobDetailRow } from './SyncJobDetailRow';
import type { SortDirection, SyncJobRow } from '../types';

interface SyncJobsTableProps {
  rows: SyncJobRow[];
  totalRecords: number;
  page: number;
  pageSize: number;
  sortCol: string;
  sortDir: SortDirection | '';
  isLoading: boolean;
  isError: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  onSortChange: (col: string, dir: SortDirection | '') => void;
}

interface ColumnSpec {
  key: keyof SyncJobRow & string;
  header: string;
  widthClass?: string;
}

const COLUMNS: readonly ColumnSpec[] = [
  { key: 'jobName', header: 'Name', widthClass: 'w-[150px]' },
  { key: 'jobType', header: 'Job Type', widthClass: 'w-[140px]' },
  { key: 'startDate', header: 'Start Date', widthClass: 'w-[150px]' },
  { key: 'endDate', header: 'End Date', widthClass: 'w-[150px]' },
  { key: 'noofRecordsCreated', header: 'Total Records Created', widthClass: 'w-[150px]' },
  { key: 'noofRecordsUpdated', header: 'Total Records Updated', widthClass: 'w-[150px]' },
];

function nextSortDir(current: SortDirection | '', clickedKey: string, activeKey: string): SortDirection | '' {
  if (clickedKey !== activeKey) return 'asc';
  if (current === 'asc') return 'desc';
  return '';
}

export function SyncJobsTable({
  rows,
  totalRecords,
  page,
  pageSize,
  sortCol,
  sortDir,
  isLoading,
  isError,
  onPageChange,
  onPageSizeChange,
  onSortChange,
}: SyncJobsTableProps) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  function toggleRow(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function handleSort(key: string) {
    const dir = nextSortDir(sortDir, key, sortCol);
    onSortChange(dir === '' ? '' : key, dir);
  }

  return (
    <div className="space-y-3">
      {isError ? (
        <div role="alert" className="border border-dds-red bg-red-50 p-3 text-sm text-red-700">
          Failed to load sync jobs. Please try again.
        </div>
      ) : null}

      <div className="relative w-full overflow-auto border border-cool-gray-2">
        {isLoading ? (
          <div
            className="absolute inset-0 z-10 flex items-center justify-center bg-white/70"
            aria-hidden="true"
          >
            <Spinner size="lg" />
          </div>
        ) : null}
        <table
          className="w-full min-w-[900px] border-collapse"
          aria-label="Sync jobs"
          aria-busy={isLoading || undefined}
        >
          <thead className="sticky top-0 z-10 bg-white">
            <tr>
              <th scope="col" className="w-10 px-2 h-14 bg-white shadow-[inset_0_-1px_0_0_#d0d0ce]">
                <span className="sr-only">Expand row</span>
              </th>
              {COLUMNS.map((col) => {
                const isSorted = sortCol === col.key;
                const ariaSort: 'ascending' | 'descending' | 'none' = isSorted
                  ? sortDir === 'asc'
                    ? 'ascending'
                    : sortDir === 'desc'
                      ? 'descending'
                      : 'none'
                  : 'none';
                return (
                  <th
                    key={col.key}
                    scope="col"
                    aria-sort={ariaSort}
                    className={cn(
                      'px-4 h-14 text-left align-middle bg-white shadow-[inset_0_-1px_0_0_#d0d0ce] whitespace-nowrap',
                      col.widthClass,
                    )}
                  >
                    <button
                      type="button"
                      onClick={() => handleSort(col.key)}
                      className="inline-flex items-center gap-1 text-xs font-semibold text-cool-gray-11 hover:text-black focus:outline-none focus:ring-2 focus:ring-black focus:ring-offset-1"
                    >
                      {col.header}
                      {isSorted && sortDir === 'asc' ? (
                        <ArrowUp aria-hidden="true" size={12} />
                      ) : isSorted && sortDir === 'desc' ? (
                        <ArrowDown aria-hidden="true" size={12} />
                      ) : (
                        <ArrowUpDown aria-hidden="true" size={12} className="opacity-50" />
                      )}
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {!isLoading && rows.length === 0 ? (
              <tr>
                <td
                  colSpan={COLUMNS.length + 1}
                  className="px-4 py-8 text-center text-sm text-cool-gray-11"
                >
                  No sync jobs found for the selected range.
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const id = row.syncJobId || `${row.jobName}|${row.startDate}|${row.endDate}`;
                const isOpen = expanded.has(id);
                const detailId = `sync-job-detail-${id}`;
                return (
                  <Fragment key={id}>
                    <tr className="hover:bg-gray-50">
                      <td className="w-10 px-2 h-12 align-middle border-b border-cool-gray-2">
                        <button
                          type="button"
                          onClick={() => toggleRow(id)}
                          aria-expanded={isOpen}
                          aria-controls={detailId}
                          aria-label={isOpen ? `Collapse ${row.jobName} details` : `Expand ${row.jobName} details`}
                          className="inline-flex h-7 w-7 items-center justify-center rounded text-cool-gray-11 hover:bg-gray-100 hover:text-black focus:outline-none focus:ring-2 focus:ring-black"
                        >
                          {isOpen ? (
                            <ChevronDown size={16} aria-hidden="true" />
                          ) : (
                            <ChevronRight size={16} aria-hidden="true" />
                          )}
                        </button>
                      </td>
                      {COLUMNS.map((col) => (
                        <td
                          key={col.key}
                          className="px-4 h-12 text-sm text-black align-middle border-b border-cool-gray-2 whitespace-nowrap"
                        >
                          {String(row[col.key] ?? '')}
                        </td>
                      ))}
                    </tr>
                    {isOpen ? (
                      <tr id={detailId}>
                        <td
                          colSpan={COLUMNS.length + 1}
                          className="px-4 py-4 bg-gray-50 border-b border-cool-gray-2"
                        >
                          <SyncJobDetailRow row={row} />
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        page={page}
        pageSize={pageSize}
        totalRecords={totalRecords}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />

      <p className="text-right text-sm italic text-cool-gray-11">
        Total Records: <span className="font-semibold not-italic text-black">{totalRecords}</span>
      </p>
    </div>
  );
}
